import { useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Paper, Chip,
    Divider, FormControlLabel, Checkbox, TextField, LinearProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Rīgas domes publisko tualešu datu importa vadības panelis.
 *
 * Datu avots — publiska Google My Maps karte, uz kuru atsauc mvd.riga.lv publiskās
 * tualešu lapa. Serveris ielādē KML no
 * https://www.google.com/maps/d/kml?mid={MID}&forcekml=1, izparsē placemarkus un
 * upsert'ē DB pēc slugified nosaukuma. Nav nepieciešams Nominatim geocoding, jo
 * koordinātes ir tieši KML placemarku Point/coordinates laukos.
 *
 * Server endpoints (AdminRigaDomeController.cs):
 *   GET  /api/v1/admin/external/riga-dome/status
 *   POST /api/v1/admin/external/riga-dome/import
 */
type RigaDomeStatus = {
    totalCount: number;
};

type ImportResponse = {
    isSuccess: boolean;
    error: string | null;
    parsed: number;
    inserted: number;
    updated: number;
    skipped: number;
    warnings: string[];
};


/**
 * Rīgas domes oficiālās My Maps kartes ID. Iegūts no mvd.riga.lv iebedded iframe
 * ("mid" URL parametrs). Ja Rīgas dome pārceļ karti, atjaunino šo konstanti — visi
 * pārējie mehānismi paliek nemainīgi.
 */
const RIGA_DOME_MID = '135hwA76FhIxcvcH6s8SlE6UbI1pGA8A';


export function RigaDomeImportPage() {
    const queryClient = useQueryClient();
    const [midInput, setMidInput] = useState(RIGA_DOME_MID);
    const [dryRun, setDryRun] = useState(true);
    const [importResult, setImportResult] = useState<ImportResponse | null>(null);

    const { data: status, refetch, isFetching } = useQuery({
        queryKey: ['admin-riga-dome-status'],
        queryFn: async () => {
            const resp = await api.get<RigaDomeStatus>('/api/v1/admin/external/riga-dome/status');
            return resp.data;
        },
    });

    const importMutation = useMutation({
        mutationFn: async (args: { myMapsMid: string; dryRun: boolean }) => {
            // KML ielāde no Google + XML parsēšana ir sekunžu jautājums (līdz 5 sec),
            // bet uzstādām 60s timeout, lai izturētu lēnas Google atbildes.
            const resp = await api.post<ImportResponse>(
                '/api/v1/admin/external/riga-dome/import',
                { ...args },
                { timeout: 60_000 },
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Imports neizdevās.');
            return resp.data;
        },
        onSuccess: (data) => {
            setImportResult(data);
            queryClient.invalidateQueries({ queryKey: ['admin-riga-dome-status'] });
        },
    });


    const canImport = midInput.trim().length > 0 && !importMutation.isPending;
    const isDefaultMid = midInput.trim() === RIGA_DOME_MID;


    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Rīgas domes tualešu imports</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Serveris ielādē KML no Google My Maps kartes, izparsē placemarkus un
                        ievieto DB. Nav geocoding — koordinātes ir tieši KML failā.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        label={status
                            ? `${status.totalCount} ierakstu DB`
                            : 'Ielādē statusu…'}
                        color={status && status.totalCount > 0 ? 'success' : 'default'}
                    />
                    <Button
                        variant="text"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        Atjaunot
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>1. Google My Maps kartes ID</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Iebedded iframe URL <code>https://www.google.com/maps/d/embed?mid=<b>&lt;MID&gt;</b></code>{' '}
                    — MID parametrs identificē konkrētu publisku My Maps karti. Default vērtība
                    ir Rīgas domes oficiālā karte no mvd.riga.lv.
                </Typography>

                <TextField
                    label="My Maps MID"
                    value={midInput}
                    onChange={(e) => setMidInput(e.target.value)}
                    fullWidth
                    slotProps={{ input: { style: { fontFamily: 'monospace' } } }}
                    helperText={isDefaultMid
                        ? 'Default: Rīgas domes oficiālā karte.'
                        : 'Mainīts no default — pārbaudi, ka mid ir pareizs.'}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>2. Palaid importu</Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={dryRun}
                            onChange={(e) => setDryRun(e.target.checked)}
                        />
                    }
                    label="Dry-run (ielādē un parsē KML, bet neieraksta DB)"
                />

                <Stack direction="row" spacing={2} mt={2} alignItems="center">
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowIcon />}
                        disabled={!canImport}
                        onClick={() => {
                            setImportResult(null);
                            importMutation.mutate({
                                myMapsMid: midInput.trim(),
                                dryRun,
                            });
                        }}
                    >
                        {dryRun ? 'Palaist dry-run' : 'Importēt DB'}
                    </Button>
                    {importMutation.isPending && (
                        <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress />
                            <Typography variant="caption" color="text.secondary" mt={0.5}>
                                Ielādē KML no Google + parsē placemarkus…
                            </Typography>
                        </Box>
                    )}
                </Stack>

                {importMutation.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        Imports neizdevās: {(importMutation.error as Error).message}
                    </Alert>
                )}
            </Paper>

            {importResult && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {dryRun ? 'Dry-run rezultāts' : 'Importa rezultāts'}
                    </Typography>

                    <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
                        <Chip label={`Parsēti: ${importResult.parsed}`} color="info" />
                        {importResult.skipped > 0 && (
                            <Chip label={`Izlaisti: ${importResult.skipped}`} color="warning" />
                        )}
                        {!dryRun && (
                            <>
                                <Chip label={`Jauni: ${importResult.inserted}`} color="success" />
                                <Chip label={`Atjaunināti: ${importResult.updated}`} color="primary" />
                            </>
                        )}
                    </Stack>

                    {importResult.warnings.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>Brīdinājumi</Typography>
                            <Box
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.85em',
                                    bgcolor: 'grey.100',
                                    p: 2,
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: 'auto',
                                }}
                            >
                                {importResult.warnings.map((w, i) => (
                                    <div key={i}>• {w}</div>
                                ))}
                            </Box>
                        </>
                    )}

                    {dryRun && importResult.parsed > 0 && !importMutation.isPending && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Dry-run beidzās veiksmīgi. Noņem "Dry-run" ķeksīti un palaid vēlreiz,
                            lai īsti ierakstītu DB.
                        </Alert>
                    )}

                    {!dryRun && (importResult.inserted > 0 || importResult.updated > 0) && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            DB veiksmīgi atjaunināta — Rīgas kartes lietotāji redzēs jaunās
                            vietas pie nākamā app palaišanas.
                        </Alert>
                    )}
                </Paper>
            )}

            {importMutation.isPending && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
}
